/**
 * LivedItSheet — Capture or edit a "Lived Dream" memory (text-only).
 *
 * Modes:
 *  - "complete" (default when goal not yet completed): tapping "Lived it" both
 *    completes the goal (sets completedAt + awards goal points) AND captures
 *    the optional memory + dream-lived bonus. A "Just mark done" secondary
 *    saves with no caption/location (date defaults to today).
 *  - "edit": goal is already lived; only memory fields are saved, no points.
 *
 * Memory fields (caption + location) are collapsed by default to keep the
 * sheet a one-tap action. Photo-free by design (no storage costs).
 */
import { useEffect, useState } from "react";
import {
  AppDialog,
  AppDialogScroll,
  AppDialogHeader,
  AppDialogBody,
  AppDialogFooter,
} from "@/components/ui/app-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Calendar, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { Goal } from "@/types/todo";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { usePoints } from "@/hooks/usePoints";
import { useCelebration } from "@/hooks/useCelebration";
import { logger } from "@/utils/logger";

interface LivedItSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal: Goal;
  onUpdate?: (updatedGoal: Goal) => void;
}

const DREAM_LIVED_BONUS = 15;
const MAX_CAPTION = 240;
const MAX_LOCATION = 80;

export function LivedItSheet({ open, onOpenChange, goal, onUpdate }: LivedItSheetProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { awardPoints, addGoalPoints } = usePoints();
  const { triggerCelebration } = useCelebration();

  const isEditMode = !!goal.livedAt;

  const today = new Date().toISOString().slice(0, 10);
  const [livedDate, setLivedDate] = useState<string>(today);
  const [caption, setCaption] = useState("");
  const [location, setLocation] = useState("");
  const [showMemoryFields, setShowMemoryFields] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset form whenever the sheet opens
  useEffect(() => {
    if (open) {
      setLivedDate(goal.livedAt ? goal.livedAt.slice(0, 10) : today);
      setCaption(goal.memoryCaption || "");
      setLocation(goal.memoryLocation || "");
      // In edit mode: show fields by default (user came here to edit them).
      // In complete mode: keep collapsed for a one-tap experience.
      setShowMemoryFields(isEditMode);
    }
  }, [open, goal.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Save handler.
   * @param skipMemory  When true, ignore caption/location even if user typed them
   *                    — used by the "Just mark done" path.
   */
  const handleSave = async (skipMemory = false) => {
    if (!user?.id || !goal.id) return;
    setSubmitting(true);
    try {
      const livedAtIso = new Date(`${livedDate}T12:00:00`).toISOString();
      const completedAtIso = goal.completedAt || livedAtIso;
      const finalCaption = skipMemory ? null : (caption.trim() || null);
      const finalLocation = skipMemory ? null : (location.trim() || null);

      const { error } = await supabase
        .from("goals")
        .update({
          lived_at: livedAtIso,
          memory_caption: finalCaption,
          memory_location: finalLocation,
          completed_at: completedAtIso,
          progress: 100,
        })
        .eq("id", goal.id)
        .eq("user_id", user.id);

      if (error) throw error;

      const wasAlreadyLived = !!goal.livedAt;
      const wasAlreadyCompleted = !!goal.completedAt;

      const updated: Goal = {
        ...goal,
        livedAt: livedAtIso,
        memoryCaption: finalCaption,
        memoryLocation: finalLocation,
        completedAt: completedAtIso,
        progress: 100,
      };
      onUpdate?.(updated);

      // First-time completion: award goal-completion points (idempotent server-side).
      if (!wasAlreadyCompleted) {
        await addGoalPoints(goal.id);
      }

      // First-time "lived" capture: award the dream bonus + celebrate once.
      if (!wasAlreadyLived) {
        await awardPoints(
          DREAM_LIVED_BONUS,
          "dream_lived",
          goal.id,
          `Lived a dream: ${goal.title}`,
        );
        if (!goal.celebratedAt) {
          setTimeout(() => triggerCelebration(goal.title, DREAM_LIVED_BONUS, goal.id), 100);
        }
        toast({
          title: "✨ Done — memory saved!",
          description: skipMemory
            ? "Saved to your Lived gallery."
            : `+${DREAM_LIVED_BONUS} points — your memory is saved.`,
          duration: 3000,
        });
      } else {
        toast({
          title: "Memory updated",
          description: "Your dream memory was saved.",
          duration: 2500,
        });
      }

      onOpenChange(false);
    } catch (err: any) {
      logger.error("[LivedItSheet] Failed to save lived dream:", err);
      toast({
        title: "Could not save",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const primaryLabel = isEditMode
    ? submitting ? "Saving…" : "Update memory"
    : submitting ? "Saving…" : "Lived it ✨";

  return (
    <AppDialog
      open={open}
      onClose={() => onOpenChange(false)}
      size="md"
      mobileSheet
      ariaTitle={isEditMode ? "Edit your memory" : "Did this one?"}
      className="bg-background/95 supports-[backdrop-filter]:bg-background/85 supports-[backdrop-filter]:backdrop-blur-2xl"
    >
      <AppDialogHeader
        icon={<span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-xl" aria-hidden>✨</span>}
        title={
          <span className="font-[Outfit] tracking-tight text-foreground">
            {isEditMode ? "Edit your memory" : "Did this one?"}
          </span>
        }
        description={
          <span className="text-muted-foreground">
            {isEditMode
              ? <>Update what you remember about <span className="font-semibold">{goal.title}</span>.</>
              : <>Tap once to mark <span className="font-semibold">{goal.title}</span> as lived. Add a tiny memory if you like — or skip it.</>}
          </span>
        }
      />
      <AppDialogScroll>
        <AppDialogBody>
          <div className="space-y-4">
            {/* Date is always visible — it's a single tap to change and meaningful. */}
            <div className="gd-well space-y-2 p-4">
              <Label htmlFor="lived-date" className="flex items-center gap-1.5 text-sm font-medium">
                <Calendar className="h-4 w-4" /> When?
              </Label>
              <Input
                id="lived-date"
                type="date"
                value={livedDate}
                max={today}
                onChange={(e) => setLivedDate(e.target.value)}
                className="bg-background"
              />
            </div>

            {/* Memory fields collapsed by default in complete mode */}
            {!showMemoryFields ? (
              <button
                type="button"
                onClick={() => setShowMemoryFields(true)}
                className="gd-well w-full min-h-11 px-4 py-3 text-left text-sm font-medium text-foreground/80 hover:text-foreground inline-flex items-center gap-1.5"
              >
                <ChevronDown className="h-4 w-4" />
                Add a memory (optional)
              </button>
            ) : (
              <div className="gd-well space-y-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="lived-caption" className="text-sm font-medium">
                    How did it feel? <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Textarea
                    id="lived-caption"
                    placeholder="One sentence. A feeling, a moment, a tiny detail you don't want to forget…"
                    value={caption}
                    maxLength={MAX_CAPTION}
                    onChange={(e) => setCaption(e.target.value)}
                    rows={3}
                    className="bg-background resize-none"
                  />
                  <div className="text-xs text-muted-foreground text-right">
                    {caption.length}/{MAX_CAPTION}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lived-location" className="flex items-center gap-1.5 text-sm font-medium">
                    <MapPin className="h-4 w-4" /> Where? <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="lived-location"
                    placeholder="e.g. Jebel Jais, Ras Al Khaimah"
                    value={location}
                    maxLength={MAX_LOCATION}
                    onChange={(e) => setLocation(e.target.value)}
                    className="bg-background"
                  />
                </div>

                {!isEditMode && (
                  <button
                    type="button"
                    onClick={() => setShowMemoryFields(false)}
                    className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:underline"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                    Hide memory
                  </button>
                )}
              </div>
            )}
          </div>
        </AppDialogBody>
      </AppDialogScroll>

      <AppDialogFooter align="between">
        {!isEditMode ? (
          <Button
            variant="ghost"
            onClick={() => handleSave(true)}
            disabled={submitting}
            className="rounded-2xl text-muted-foreground hover:text-foreground"
          >
            Just mark done
          </Button>
        ) : (
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
        )}
        <Button
          onClick={() => handleSave(false)}
          disabled={submitting}
          className="h-11 rounded-2xl px-5 shadow-[var(--gd-shadow-1)]"
        >
          <Sparkles className="h-4 w-4 mr-1.5" />
          {primaryLabel}
        </Button>
      </AppDialogFooter>
    </AppDialog>

  );
}
